import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-hot-toast'
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Button,
  Text,
  Textarea,
  Checkbox,
  makeStyles,
  tokens,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Spinner,
  Tooltip
} from '@fluentui/react-components'
import { Info24Regular } from '@fluentui/react-icons'
import logger from '../utils/logger';
import { fetchWithIap } from '../utils/iapService';

const useStyles = makeStyles({
  dialogSurface: {
    width: '95vw',
    maxWidth: '480px',
    padding: '9px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column'
  },
  dialogTitle: {
    paddingBottom: '4px',
    fontSize: '18px',
    fontWeight: tokens.fontWeightSemibold,
    flexShrink: 0
  },
  dialogBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
    width: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    flexGrow: 1,
    paddingRight: '4px'
  },
  accordionItem: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    marginBottom: '8px',
    overflow: 'hidden',
    boxShadow: tokens.shadow2
  },
  accordionHeader: {
    padding: '0px',
    '& .fui-AccordionHeader__button': {
      width: '100%',
      padding: '8px 12px',
      justifyContent: 'start',
      border: 'none',
      transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
      "&[data-fui-accordion-header-open='true']": {
        backgroundColor: `${tokens.colorBrandBackground} !important`,
        color: `${tokens.colorNeutralForegroundOnBrand} !important`,
        fontWeight: tokens.fontWeightSemibold
      },
      "&[data-fui-accordion-header-open='false']": {
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        fontWeight: tokens.fontWeightRegular
      }
    }
  },
  accordionPanel: {
    padding: '0px 6px 6px 6px',
    animationName: 'fade-in-slide-down',
    animationDuration: '0.3s',
    animationTimingFunction: 'ease-in-out'
  },
  disclaimer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    marginTop: '10px',
    backgroundColor: '#fffbdd',
    borderLeft: `4px solid #fce100`,
    borderRadius: '4px',
    color: tokens.colorNeutralForeground1
  },
  dialogActions: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    gap: '8px',
    paddingTop: '6px',
    flexShrink: 0,
    '& > button': {
      flex: 1
    }
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  textArea: {
    width: '100%'
  },
  fileListContainer: {
    padding: '4px',
    maxHeight: '135px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  checkboxContainer: {
    maxWidth: '100%'
  },
  truncatedLabel: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '320px'
  },
  metricsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0px',
    minHeight: '50px',
    alignItems: 'start'
  },
  mandatoryLabel: {
    color: '#d13438',
    fontWeight: 'bold',
    marginLeft: '2px'
  },
  '@keyframes fade-in-slide-down': {
    from: {
      opacity: 0,
      transform: 'translateY(-10px)'
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
})

// The API endpoint for fetching metrics
const API_FEEDBACK_METRICS_ENDPOINT = '/api/feedback/metrics'

/**
 * FeedbackModal Component
 *
 * A detailed modal for collecting structured feedback when a user dislikes a response.
 * It includes sections for selecting relevant files, choosing from a dynamic list of
 * issues, and providing detailed comments. It fetches feedback options from an API
 * dynamically when it opens based on the current data source.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.isOpen - Controls whether the modal is visible.
 * @param {Function} props.onClose - Callback triggered when the user dismisses the modal.
 * @param {Function} props.onSubmit - Callback triggered when valid feedback is submitted.
 * @param {string[]} props.sourceFiles - List of available file names the user can flag as problematic.
 * @param {string} props.dataSource - The current active source (e.g., 'file_upload', 'DocLib') used to fetch specific metrics.
 * @param {string} props.sessionId - Unique session identifier passed to API headers.
 * @returns {JSX.Element} The rendered FeedbackModal component.
 */
const FeedbackModal = ({
  isOpen,
  onClose,
  onSubmit,
  sourceFiles,
  dataSource,
  sessionId
}) => {
  const styles = useStyles()
  const [comment, setComment] = useState('')
  const [selectedFileNames, setSelectedFileNames] = useState([])
  const [expectedResponse, setExpectedResponse] = useState('')
  const [selectedMetrics, setSelectedMetrics] = useState([])
  const [commentError, setCommentError] = useState(false)
  const [expectedResponseError, setExpectedResponseError] = useState(false)
  const [openAccordionItems, setOpenAccordionItems] = useState([])
  const [dynamicMetrics, setDynamicMetrics] = useState([])
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false)
  const [metricsError, setMetricsError] = useState(null)

  /**
  * Effect hook that manages the modal's lifecycle.
  * When opened, it fetches relevant metrics from the backend and expands the correct accordion.
  * When closed, it resets all internal state values to ensure a clean slate for the next open.
  */
  useEffect(
    () => {
      /**
   * Fetches the dynamic feedback metrics/options from the backend
   * based on the currently active data source.
   */
      const fetchMetrics = async () => {
        if (!dataSource || !sessionId) {
          console.warn(
            'Missing dataSource or sessionId; cannot fetch feedback metrics.'
          )
          setDynamicMetrics([])
          return
        }
        const apiTag = `GET ${API_FEEDBACK_METRICS_ENDPOINT}`
        const requestParams = { data_source: dataSource }

        logger.info('Fetching feedback metrics.', {
          functionName: 'fetchMetrics',
          api: apiTag,
          requestParams
        })

        setIsLoadingMetrics(true)
        setMetricsError(null)
        setDynamicMetrics([])

        try {
          const url = `${API_FEEDBACK_METRICS_ENDPOINT}?data_source=${encodeURIComponent(
            dataSource
          )}`
          const response = await fetchWithIap(url, {
            method: 'GET',
            headers: {
              'X-Session-ID': sessionId
            }
          })

          if (!response.ok) {
            const responseBody = await response.json().catch(() => null)
            logger.error('Failed to fetch feedback metrics.', {
              functionName: 'fetchMetrics',
              api: apiTag,
              status: response.status,
              errorPayload: responseBody
            })
            throw new Error(
              `Failed to fetch feedback options (Status: ${response.status})`
            )
          }
          const data = await response.json()
          logger.info('Successfully fetched feedback metrics.', {
            functionName: 'fetchMetrics',
            api: apiTag,
            responsePayload: data
          })
          setDynamicMetrics(data.metrics || [])
        } catch (error) {
          console.error('Error fetching feedback metrics:', error)
          logger.error('The feedback metrics fetch process failed.', {
            functionName: 'fetchMetrics',
            originalError: error // Capture the full error object with stack trace
          })
          setMetricsError('Could not load feedback options.')
        } finally {
          setIsLoadingMetrics(false)
        }
      }

      if (isOpen) {
        fetchMetrics()
        if (
          dataSource === 'file_upload' &&
          sourceFiles &&
          sourceFiles.length > 0
        ) {
          setOpenAccordionItems(['1'])
        } else {
          setOpenAccordionItems(['2'])
        }
      } else {
        // When it closes, reset all state
        setComment('')
        setSelectedFileNames([])
        setExpectedResponse('')
        setSelectedMetrics([])
        setCommentError(false)
        setExpectedResponseError(false)
        setOpenAccordionItems([])
        setDynamicMetrics([]) // Clear fetched metrics
        setIsLoadingMetrics(false)
        setMetricsError(null)
      }
    },
    [isOpen]
  )

  /**
  * Handles the toggling of accordion items.
  *
  * @param {Event} event - The DOM event triggered by the toggle.
  * @param {Object} data - An object containing the `openItems` array from Fluent UI.
  */
  const handleAccordionToggle = (event, data) => {
    setOpenAccordionItems(data.openItems)
  }

  /**
  * Toggles a specific file's selection status in the local state array.
  *
  * @param {string} fileName - The name of the file to select or deselect.
  */
  const handleCheckboxChange = fileName => {
    setSelectedFileNames(
      prev =>
        prev.includes(fileName)
          ? prev.filter(name => name !== fileName)
          : [...prev, fileName]
    )
  }

  /**
  * Toggles a specific feedback metric's selection status in the local state array.
  *
  * @param {string} metric - The text of the metric/issue to select or deselect.
  */
  const handleMetricChange = metric => {
    setSelectedMetrics(
      prev =>
        prev.includes(metric)
          ? prev.filter(m => m !== metric)
          : [...prev, metric]
    )
  }

  /**
   * Validates and submits the feedback form.
   * Both the comment and expected response fields are mandatory.
   */
  const handleSubmit = () => {
    const isCommentEmpty = comment.trim() === ''
    const isExpectedResponseEmpty = expectedResponse.trim() === ''
    // Update UI to highlight errors
    setCommentError(isCommentEmpty)
    setExpectedResponseError(isExpectedResponseEmpty)
    // Check if either field failed validation
    if (isCommentEmpty || isExpectedResponseEmpty) {
      toast.error(
        'Please provide a comment and describe the expected response.'
      )
      setOpenAccordionItems(['3'])
      return
    }
    onSubmit({
      comment,
      selectedFiles: selectedFileNames,
      expectedResponse,
      metrics: selectedMetrics
    })
  }

  /**
  * Wrapper function to trigger the parent's `onClose` callback.
  */
  const handleClose = () => {
    onClose()
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(e, data) => !data.open && handleClose()}
    >
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody className={styles.dialogBody}>
          <DialogTitle className={styles.dialogTitle}>
            Provide Feedback
          </DialogTitle>

          <Accordion
            // multiple
            collapsible
            openItems={openAccordionItems}
            onToggle={handleAccordionToggle}
          >
            {sourceFiles &&
              sourceFiles.length > 0 &&
              <AccordionItem value='1' className={styles.accordionItem}>
                <AccordionHeader className={styles.accordionHeader}>
                  Select Relevant Files
                </AccordionHeader>
                <AccordionPanel className={styles.accordionPanel}>
                  <div className={styles.fileListContainer}>
                    {sourceFiles.map(file =>
                      <Tooltip key={file} content={file} relationship='label'>
                        <Checkbox
                          className={styles.checkboxContainer}
                          label={
                            <span className={styles.truncatedLabel}>
                              {file}
                            </span>
                          }
                          checked={selectedFileNames.includes(file)}
                          onChange={() => handleCheckboxChange(file)}
                        />
                      </Tooltip>
                    )}
                  </div>
                  <div className={styles.disclaimer}>
                    <Info24Regular />
                    <Text size={200}>
                      Disclaimer: Selected files will be saved by AIScribe for
                      analysis and quality assurance purposes.
                    </Text>
                  </div>
                </AccordionPanel>
              </AccordionItem>}

            <AccordionItem value='2' className={styles.accordionItem}>
              <AccordionHeader className={styles.accordionHeader}>
                What went wrong?
              </AccordionHeader>
              <AccordionPanel className={styles.accordionPanel}>
                <div className={styles.metricsContainer}>
                  {/* Conditional Rendering Logic */}
                  {isLoadingMetrics
                    ? <Spinner size='small' label='Loading options...' />
                    : metricsError
                      ? <Text wrap={false} style={{ color: '#d13438' }}>
                        {metricsError}
                      </Text>
                      : dynamicMetrics.length > 0
                        ? // Map over the dynamically fetched metrics
                          dynamicMetrics.map(metric =>
                            <Checkbox
                              key={metric}
                              label={metric}
                              checked={selectedMetrics.includes(metric)}
                              onChange={() => handleMetricChange(metric)}
                            />
                          )
                        : <Text>
                            No specific feedback options available for this
                            source.
                          </Text>}
                </div>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem value='3' className={styles.accordionItem}>
              <AccordionHeader className={styles.accordionHeader}>
                Additional Details<span className={styles.mandatoryLabel}>
                  *
                </span>
              </AccordionHeader>
              <AccordionPanel className={styles.accordionPanel}>
                <div className={styles.inputGroup}>
                  <Text weight='semibold'>Comment</Text>
                  <Textarea
                    className={styles.textArea}
                    style={commentError ? { border: '2px solid #d13438' } : {}}
                    resize='vertical'
                    placeholder='Provide more details about the issue...'
                    value={comment}
                    onChange={e => {
                      setComment(e.target.value)
                      if (commentError && e.target.value.trim() !== '') {
                        setCommentError(false)
                      }
                    }}
                  />
                </div>

                <div className={styles.inputGroup} style={{ marginTop: '4px' }}>
                  <Text weight='semibold'>Expected Response</Text>
                  <Textarea
                    className={styles.textArea}
                    style={
                      expectedResponseError
                        ? { border: '2px solid #d13438' }
                        : {}
                    }
                    resize='vertical'
                    placeholder='Describe the response you expected to see...'
                    value={expectedResponse}
                    onChange={e => {
                      setExpectedResponse(e.target.value)
                      if (
                        expectedResponseError &&
                        e.target.value.trim() !== ''
                      ) {
                        setExpectedResponseError(false)
                      }
                    }}
                  />
                </div>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>

          <DialogActions className={styles.dialogActions}>
            <Button appearance='secondary' onClick={handleClose}>
              Cancel
            </Button>
            <Button appearance='primary' onClick={handleSubmit}>
              Submit Feedback
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

FeedbackModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  sourceFiles: PropTypes.arrayOf(PropTypes.string),
  dataSource: PropTypes.string,
  sessionId: PropTypes.string.isRequired
}

export default FeedbackModal
